<script setup>
import { ref, computed } from 'vue';
import { VueFlow, MarkerType, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import FlowStore from './FlowStore.vue';

const props = defineProps({
  nodeTypes: {
    type: Object,
    required: true,
  },
  nodes: {
    type: Array,
    default: () => [],
  },
  edges: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:nodes',
  'update:edges',
  'nodeClick',
  'paneClick',
  'drop',
  'connect',
  'edgeClick',
  'nodeDragStop',
]);

const flowStore = ref(null);

const nodesModel = computed({
  get: () => props.nodes,
  set: value => emit('update:nodes', value),
});

const edgesModel = computed({
  get: () => props.edges,
  set: value => emit('update:edges', value),
});

const onDragOver = event => {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
};

const onDrop = event => {
  event.preventDefault();
  if (!event.dataTransfer || !flowStore.value) return;
  const type = event.dataTransfer.getData('text/plain');
  const position = flowStore.value.screenToFlowCoordinate({
    x: event.clientX,
    y: event.clientY,
  });
  emit('drop', { type, position });
};

const onNodeClick = ({ node }) => {
  emit('nodeClick', { node });
};

const onPaneClick = () => {
  emit('paneClick');
};

const onConnect = connection => {
  emit('connect', connection);
};

const onEdgeClick = ({ edge }) => {
  emit('edgeClick', edge);
};

const { findNode, getNodes } = useVueFlow();

const onNodeDragStop = ({ node }) => {
  if (node.type === 'group') {
    emit('nodeDragStop', { node });
    return;
  }

  const groupNodes = getNodes.value.filter(n => n.type === 'group');
  if (!groupNodes.length) {
    if (node.parentNode) {
      const internalNode = findNode(node.id);
      let absX = node.position.x;
      let absY = node.position.y;
      const parent = findNode(node.parentNode);
      if (parent) {
        absX += parent.position.x;
        absY += parent.position.y;
      }
      if (internalNode) {
        internalNode.parentNode = undefined;
        internalNode.position = { x: absX, y: absY };
      }
      emit('nodeDragStop', {
        node,
        groupId: null,
        absolutePosition: { x: absX, y: absY },
      });
    } else {
      emit('nodeDragStop', { node });
    }
    return;
  }

  const nodeW = node.dimensions?.width || 180;
  const nodeH = node.dimensions?.height || 80;

  let nodeAbsX = node.position.x;
  let nodeAbsY = node.position.y;
  if (node.parentNode) {
    const parent = findNode(node.parentNode);
    if (parent) {
      nodeAbsX += parent.position.x;
      nodeAbsY += parent.position.y;
    }
  }

  const containingGroup = groupNodes.find(g => {
    const gx = g.position.x;
    const gy = g.position.y;
    const gw = g.dimensions?.width || parseInt(g.style?.width, 10) || 400;
    const gh = g.dimensions?.height || parseInt(g.style?.height, 10) || 250;
    return (
      nodeAbsX >= gx &&
      nodeAbsX + nodeW <= gx + gw &&
      nodeAbsY >= gy &&
      nodeAbsY + nodeH <= gy + gh
    );
  });

  const internalNode = findNode(node.id);
  if (!internalNode) {
    emit('nodeDragStop', { node });
    return;
  }

  if (containingGroup) {
    internalNode.parentNode = containingGroup.id;
    internalNode.position = {
      x: nodeAbsX - containingGroup.position.x,
      y: nodeAbsY - containingGroup.position.y,
    };
    emit('nodeDragStop', {
      node,
      groupId: containingGroup.id,
      groupPosition: containingGroup.position,
      relativePosition: {
        x: nodeAbsX - containingGroup.position.x,
        y: nodeAbsY - containingGroup.position.y,
      },
    });
  } else if (node.parentNode) {
    internalNode.parentNode = undefined;
    internalNode.position = { x: nodeAbsX, y: nodeAbsY };
    emit('nodeDragStop', {
      node,
      groupId: null,
      absolutePosition: { x: nodeAbsX, y: nodeAbsY },
    });
  } else {
    emit('nodeDragStop', { node });
  }
};
</script>

<template>
  <div
    class="relative h-full flex-1 min-w-0 bg-n-slate-3 dark:bg-n-slate-4"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <VueFlow
      v-model:nodes="nodesModel"
      v-model:edges="edgesModel"
      :node-types="nodeTypes"
      :default-viewport="{ zoom: 1 }"
      :default-edge-options="{
        type: 'bezier',
        markerEnd: MarkerType.ArrowClosed,
        style: { strokeWidth: 1 },
      }"
      fit-view-on-init
      class="h-full w-full"
      @node-click="onNodeClick"
      @pane-click="onPaneClick"
      @connect="onConnect"
      @edge-click="onEdgeClick"
      @node-drag-stop="onNodeDragStop"
    >
      <FlowStore ref="flowStore" />
      <Background pattern-color="#aaa" :gap="20" />
      <Controls />
      <MiniMap />
    </VueFlow>
  </div>
</template>

<style scoped>
:deep(.vue-flow__node-group) {
  pointer-events: none !important;
}
</style>
